import { observer } from 'mobx-react-lite'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import CheckOutlined from '@mui/icons-material/CheckOutlined'
import ButtonWithProgress from 'reusable/mui/progress-button/ButtonWithProgress'
import { themeConfig } from '@gap/shared/theme'
import { ChargebeeDto } from '@gap/backend/app-services/chargebee/models/ChargebeeDto'
import { useBillingStore } from './BillingStore'
import { formatBillingAmount } from './billing-format'

const formatAmount = (amount: { price?: number; currencyCode: string }): string =>
   amount.price === undefined ? 'Custom' : formatBillingAmount({ amount: amount.price, currencyCode: amount.currencyCode })

const formatDescription = (description: string): string => description.replace(/<br\s*\/?>/gi, '\n').trim()

export const PlanGrid = observer(function PlanGrid(props: { plans: ChargebeeDto.PurchasableItem[] }) {
   const billingStore = useBillingStore()
   if (props.plans.length === 0) {
      return <Typography variant="body1" sx={{ color: themeConfig.primary[600] }}>
         No plans available in this category.
      </Typography>
   }

   // Reserve room above the cards for the "Most popular" banner, which sits above its card (see PlanCard).
   const hasRecommended = props.plans.some(plan => !billingStore.isOnPaidPlan && (plan.isRecommended ?? false) && plan.itemPriceId !== billingStore.currentPlanItemPriceId)

   return (
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: `repeat(${props.plans.length}, 1fr)` }, gap: 1.5, pt: hasRecommended ? 4 : 0 }}>
         {props.plans.map(plan => <PlanCard plan={plan} key={plan.itemPriceId} />)}
      </Box>
   )
})

const PlanCard = observer(function PlanCard(props: { plan: ChargebeeDto.PurchasableItem }) {
   const billingStore = useBillingStore()
   const isCurrent = props.plan.itemPriceId === billingStore.currentPlanItemPriceId
   const isCustom = props.plan.isCustom ?? false
   const isUpgrade = (props.plan.price ?? 0) > billingStore.currentPlanPrice
   const canAddSeats = isCurrent && billingStore.canManageSeats
   const actionLabel = canAddSeats ? 'Add seats' : isCurrent ? 'Current plan' : isCustom ? 'Request upgrade' : isUpgrade ? 'Upgrade' : 'Downgrade'
   const isUpgradeIntent = canAddSeats || (!isCurrent && (isCustom || isUpgrade))
   const isRecommended = (props.plan.isRecommended ?? false) && !billingStore.isOnPaidPlan
   const actionHandler = canAddSeats
      ? billingStore.addSeats
      : isCustom
         ? billingStore.requestCustomPlanButtonHandler
         : billingStore.changePlanButtonHandler
   const borderColor = isRecommended && !isCurrent ? themeConfig.accents.orange[500] : themeConfig.primary[200]
   const showMostPopular = isRecommended && !isCurrent

   return (
      <Box sx={{ position: 'relative', height: '100%' }}>
         {showMostPopular && (
            <Box
               sx={{
                  position: 'absolute',
                  bottom: '100%',
                  left: 0,
                  right: 0,
                  bgcolor: themeConfig.accents.orange[500],
                  borderRadius: '16px 16px 0 0',
                  py: 0.5,
               }}
            >
               <Typography variant="body1Bold" sx={{ display: 'block', textAlign: 'center', color: '#fff' }}>
                  Most popular
               </Typography>
            </Box>
         )}
         <Stack
            sx={{
               height: '100%',
               p: 2.5,
               gap: 1,
               alignItems: 'center',
               textAlign: 'center',
               border: `${showMostPopular ? 3 : 2}px solid ${borderColor}`,
               borderRadius: showMostPopular ? '0 0 16px 16px' : '16px',
               overflow: 'hidden',
            }}
         >
            {isCurrent && (
               <Box sx={{ alignSelf: 'stretch', mx: -2.5, mt: -2.5, mb: 1, py: 0.75, bgcolor: themeConfig.light.background.alt, borderBottom: `1px solid ${themeConfig.primary[200]}` }}>
                  <Typography variant="body1" sx={{ display: 'block', textAlign: 'center', color: themeConfig.primary[900] }}>
                     Your current plan
                  </Typography>
               </Box>
            )}
            <Typography variant="subtitle2Bold">{props.plan.externalName || props.plan.name}</Typography>
            {props.plan.description && (
               <Typography variant="body2" sx={{ color: themeConfig.primary[600], whiteSpace: 'pre-line' }}>
                  {formatDescription(props.plan.description)}
               </Typography>
            )}
            <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'center' }}>
               <Typography variant="h4">{isCustom ? 'Contact us' : formatAmount(props.plan)}</Typography>
               {!isCustom && <>
                  {props.plan.periodUnit === 'month' && <Typography variant="h6">/mo</Typography>}
                  {props.plan.periodUnit !== 'month' && <Typography variant="h6">per {props.plan.periodUnit}</Typography>}
               </>}
            </Stack>
            {!isCustom && <Typography variant="caption" sx={{ color: themeConfig.primary[600] }}>
               Billed in {props.plan.currencyCode} {props.plan.currencyCode === 'NZD' ? 'plus GST' : ''}
            </Typography>}
            {props.plan.addons && props.plan.addons.length > 0 && (
               <Stack sx={{ width: '100%', gap: 0.5, mt: 1, pt: 1.5, borderTop: `1px solid ${themeConfig.primary[200]}` }}>
                  <Typography variant="caption" sx={{ color: themeConfig.primary[600], textAlign: 'left' }}>
                     Add-ons
                  </Typography>
                  {props.plan.addons.map(addon => (
                     <Stack key={addon.name} direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                        <Typography variant="body2" sx={{ color: themeConfig.primary[600], textAlign: 'left' }}>
                           {addon.name}
                        </Typography>
                        <Typography variant="body2Bold" sx={{ whiteSpace: 'nowrap' }}>
                           {isCustom ? 'Contact us' : `${formatAmount(addon)}${addon.periodUnit ? `/${addon.periodUnit === 'month' ? 'mo' : addon.periodUnit}` : ''}`}
                        </Typography>
                     </Stack>
                  ))}
               </Stack>
            )}
            {props.plan.features && props.plan.features.length > 0 && (
               <Stack sx={{ width: '100%', gap: 0.5, mt: 1, pt: 1.5, borderTop: `1px solid ${themeConfig.primary[200]}` }}>
                  <Typography variant="caption" sx={{ color: themeConfig.primary[600], textAlign: 'left' }}>
                     Features
                  </Typography>
                  {props.plan.features.map(feature => (
                     <Stack key={feature} direction="row" sx={{ gap: 0.75, alignItems: 'flex-start' }}>
                        <CheckOutlined sx={{ fontSize: 16, mt: '3px', flexShrink: 0, color: themeConfig.system.success[600] }} />
                        <Typography variant="body2" sx={{ color: themeConfig.primary[600], textAlign: 'left' }}>
                           {feature}
                        </Typography>
                     </Stack>
                  ))}
               </Stack>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <ButtonWithProgress
               variant={isUpgradeIntent ? 'contained' : 'outlined'}
               disabled={isCurrent && !canAddSeats}
               data-item-price-id={props.plan.itemPriceId}
               loading={billingStore.checkoutLoader.isLoading}
               onClick={actionHandler}
               sx={{ width: '100%', mt: 1 }}
            >
               {actionLabel}
            </ButtonWithProgress>
         </Stack>
      </Box>
   )
})